import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Empty, Badge } from "@/components/common";
import Modal from "@/components/Modal";
import { Plus, Search, Pencil, Trash2, Loader2, Eye, Upload, Download, Lock, LockOpen, FileText, UserPlus, MessageCircle } from "lucide-react";
import { StatusBadge } from "@/lib/status";

const EMPTY_POS = {
  client_company: "", position_name: "", location: "", linkedin_url: "",
  poc_name: "", poc_email: "", date: ""
};

const EMPTY_CAND = { name: "", phone: "", email: "", location: "", rate: "" };

export default function PositionsPage() {
  const { user } = useAuth();
  const isRecruiter = user.role === "recruiter";
  const canCreate = user.role === "admin" || user.role === "research_analyst";
  const canSubmitCandidate = user.role === "admin" || user.role === "recruiter";

  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("open"); // open | closed | all
  const [error, setError] = useState("");

  // Create/Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POS);
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [newComment, setNewComment] = useState("");

  // Candidate submission modal
  const [candOpen, setCandOpen] = useState(false);
  const [candForm, setCandForm] = useState(EMPTY_CAND);
  const [candResume, setCandResume] = useState(null);
  const [candSubmitting, setCandSubmitting] = useState(false);
  const [candTargetPosition, setCandTargetPosition] = useState(null);

  const fetchPositions = async () => {
    try {
      const { data } = await api.get("/positions");
      setPositions(data);
    } catch (e) { setError(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchPositions(); }, []);

  const filtered = positions.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [p.client_company, p.position_name, p.location, p.poc_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  // ---- create/edit ----
  const openCreate = () => { setEditing(null); setForm(EMPTY_POS); setError(""); setEditOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_POS, ...p }); setError(""); setEditOpen(true); };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError("");
    try {
      if (editing) await api.put(`/positions/${editing.id}`, form);
      else await api.post("/positions", form);
      setEditOpen(false); await fetchPositions();
    } catch (e2) { setError(formatErr(e2)); }
    finally { setSubmitting(false); }
  };

  // ---- detail ----
  const openDetail = async (p) => {
    setDetail(p); setDetailOpen(true); setCandidates([]);
    try {
      // refresh full data
      const { data } = await api.get(`/positions/${p.id}`);
      setDetail(data);
      const c = await api.get(`/candidates?position_id=${p.id}`);
      setCandidates(c.data);
    } catch (e) { /* ignore */ }
  };

  const toggleStatus = async () => {
    if (!detail) return;
    const action = detail.status === "open" ? "close" : "open";
    try {
      await api.post(`/positions/${detail.id}/${action}`);
      setDetail({ ...detail, status: action === "close" ? "closed" : "open" });
      await fetchPositions();
    } catch (e) { alert(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this position? This cannot be undone.")) return;
    try { await api.delete(`/positions/${id}`); setDetailOpen(false); await fetchPositions(); }
    catch (e) { alert(formatErr(e)); }
  };

  const uploadAgreement = async (e) => {
    const file = e.target.files?.[0]; if (!file || !detail) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/positions/${detail.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/positions/${detail.id}`); setDetail(data); await fetchPositions();
    } catch (e2) { alert(formatErr(e2)); }
    finally { e.target.value = ""; }
  };

  const downloadAgreement = async () => {
    try {
      const res = await api.get(`/positions/${detail.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = detail.agreement_file_name || "agreement";
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  const downloadResume = async (cand) => {
    try {
      const res = await api.get(`/candidates/${cand.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = cand.resume_file_name || `${cand.name}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  const addComment = async () => {
    if (!newComment.trim() || !detail) return;
    try {
      await api.post(`/positions/${detail.id}/comments`, { text: newComment });
      const { data } = await api.get(`/positions/${detail.id}`); setDetail(data); setNewComment("");
    } catch (e) { alert(formatErr(e)); }
  };

  // ---- candidate submission ----
  const openCandidate = (p) => {
    setCandTargetPosition(p); setCandForm(EMPTY_CAND); setCandResume(null); setCandOpen(true);
  };

  const submitCandidate = async (e) => {
    e.preventDefault(); setCandSubmitting(true);
    try {
      const { data } = await api.post("/candidates", { position_id: candTargetPosition.id, ...candForm });
      if (candResume) {
        const fd = new FormData(); fd.append("file", candResume);
        await api.post(`/candidates/${data.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setCandOpen(false);
      await fetchPositions();
      alert("Candidate submitted successfully.");
    } catch (e2) { alert(formatErr(e2)); }
    finally { setCandSubmitting(false); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        testId="positions-header"
        title={isRecruiter ? "Open Positions" : "Open Positions"}
        subtitle={isRecruiter ? "Browse positions and submit your candidates." : "Manage active and closed positions."}
        action={canCreate && (
          <button className="btn-primary" onClick={openCreate} data-testid="create-position-button">
            <Plus size={16} /> New Position
          </button>
        )}
      />

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <div className="card-base mb-4">
        <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-100">
          <Search size={16} className="text-gray-400" />
          <input
            data-testid="positions-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company, position, location…"
            className="flex-1 min-w-[200px] outline-none text-sm bg-transparent"
          />
          <div className="flex gap-1 bg-gray-100 rounded-md p-0.5" data-testid="status-filter">
            {["open", "closed", "all"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`px-3 py-1 rounded text-xs font-semibold transition ${filter === f ? "bg-white shadow text-brand" : "text-gray-600 hover:text-gray-900"}`}
              >
                {f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400 font-mono">{filtered.length}</span>
        </div>

        {loading ? <div className="p-8 text-gray-500 text-sm">Loading positions…</div>
          : filtered.length === 0 ? <div className="p-6"><Empty title="No positions found" /></div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="positions-table">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Client / Position</Th>
                  <Th>Location</Th>
                  {!isRecruiter && <Th>POC</Th>}
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th>Candidates</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50 group" data-testid={`position-row-${p.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{p.position_name}</div>
                      <div className="text-xs text-gray-500">{p.client_company}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{p.location || "—"}</td>
                    {!isRecruiter && (
                      <td className="py-3 px-4">
                        <div className="text-gray-900">{p.poc_name || "—"}</div>
                        <div className="text-xs text-gray-500">{p.poc_email}</div>
                      </td>
                    )}
                    <td className="py-3 px-4 text-gray-600 font-mono text-xs">{p.date || "—"}</td>
                    <td className="py-3 px-4">
                      <Badge variant={p.status === "open" ? "success" : "default"}>{p.status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="brand">{p.candidate_count}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openDetail(p)} className="btn-ghost" data-testid={`view-position-${p.id}`}>
                          <Eye size={14} /> View
                        </button>
                        {canSubmitCandidate && p.status === "open" && (
                          <button onClick={() => openCandidate(p)} className="btn-ghost text-brand" data-testid={`submit-candidate-${p.id}`}>
                            <UserPlus size={14} /> Submit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={editing ? "Edit Position" : "New Position"} testId="position-modal">
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client Company *" value={form.client_company} onChange={(v) => setForm({ ...form, client_company: v })} required testId="pos-company" />
            <Field label="Position Name *" value={form.position_name} onChange={(v) => setForm({ ...form, position_name: v })} required testId="pos-name" />
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="pos-location" />
            <Field label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} testId="pos-linkedin" />
            <Field label="POC Name" value={form.poc_name} onChange={(v) => setForm({ ...form, poc_name: v })} testId="pos-poc-name" />
            <Field label="POC Email" value={form.poc_email} onChange={(v) => setForm({ ...form, poc_email: v })} testId="pos-poc-email" />
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="pos-date" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={() => setEditOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="pos-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : (editing ? "Save changes" : "Create position")}
            </button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title="Position Details" size="lg" testId="position-detail-modal">
        {detail && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-2xl font-black tracking-tighter">{detail.position_name}</h3>
                <div className="text-sm text-gray-500 mt-1">{detail.client_company} · {detail.location || "Remote"}</div>
              </div>
              <Badge variant={detail.status === "open" ? "success" : "default"}>{detail.status}</Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {!isRecruiter && (
                <>
                  <Info label="POC Name" value={detail.poc_name} />
                  <Info label="POC Email" value={detail.poc_email} />
                </>
              )}
              <Info label="LinkedIn" value={detail.linkedin_url && <a className="text-brand underline" href={detail.linkedin_url} target="_blank" rel="noreferrer">View</a>} />
              <Info label="Date" value={detail.date} />
              <Info label="Candidates" value={candidates.length} />
            </div>

            {!isRecruiter && (
              <div className="flex flex-wrap gap-2">
                {user.role === "admin" && (
                  <>
                    <label className="btn-secondary cursor-pointer" data-testid="upload-agreement-button">
                      <Upload size={14} /> {detail.agreement_file_name ? "Replace Agreement" : "Upload Agreement"}
                      <input type="file" accept=".pdf" hidden onChange={uploadAgreement} />
                    </label>
                    {detail.agreement_file_name && (
                      <button className="btn-secondary" onClick={downloadAgreement} data-testid="download-agreement-button">
                        <Download size={14} /> {detail.agreement_file_name}
                      </button>
                    )}
                  </>
                )}
                <button className="btn-secondary" onClick={() => openEdit(detail)} data-testid="edit-position-button">
                  <Pencil size={14} /> Edit
                </button>
                <button className="btn-secondary" onClick={toggleStatus} data-testid="toggle-position-button">
                  {detail.status === "open" ? <><Lock size={14} /> Close</> : <><LockOpen size={14} /> Reopen</>}
                </button>
                {user.role === "admin" && (
                  <button className="btn-danger" onClick={() => remove(detail.id)} data-testid="delete-position-button">
                    <Trash2 size={14} /> Delete
                  </button>
                )}
              </div>
            )}

            {/* Candidates list */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading text-lg font-bold">Submitted Candidates ({candidates.length})</h4>
                {canSubmitCandidate && detail.status === "open" && (
                  <button className="btn-primary" onClick={() => openCandidate(detail)} data-testid="detail-submit-candidate">
                    <UserPlus size={14} /> Submit Candidate
                  </button>
                )}
              </div>
              {candidates.length === 0 ? (
                <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md p-4">No candidates yet.</div>
              ) : (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr><Th>Name</Th><Th>Contact</Th><Th>Recruiter</Th><Th>Status</Th><Th>Rate</Th><Th>Resume</Th></tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c.id} className="border-t border-gray-100">
                          <td className="py-2 px-4 font-medium">{c.name}</td>
                          <td className="py-2 px-4">
                            <div>{c.email}</div>
                            <div className="text-xs text-gray-500">{c.phone}</div>
                          </td>
                          <td className="py-2 px-4 text-gray-700">{c.recruiter_name}</td>
                          <td className="py-2 px-4"><StatusBadge status={c.status || "submitted"} /></td>
                          <td className="py-2 px-4 font-mono text-xs">{c.rate}</td>
                          <td className="py-2 px-4">
                            {c.resume_file_name ? (
                              <button onClick={() => downloadResume(c)} className="text-brand hover:underline text-xs" data-testid={`download-resume-${c.id}`}>
                                <FileText size={12} className="inline" /> {c.resume_file_name}
                              </button>
                            ) : <span className="text-xs text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Comments */}
            <div>
              <h4 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><MessageCircle size={16} /> Comments</h4>
              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                {(detail.comments || []).length === 0 ? (
                  <div className="text-sm text-gray-500">No comments yet.</div>
                ) : (detail.comments || []).map((c) => (
                  <div key={c.id} className="bg-gray-50 rounded-md p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{c.author_name}</span>
                      <span className="text-xs text-gray-400 font-mono">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-gray-700">{c.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="new-comment-input"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="input-base flex-1"
                />
                <button className="btn-primary" onClick={addComment} disabled={!newComment.trim()} data-testid="add-comment-button">Post</button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Submit Candidate Modal */}
      <Modal open={candOpen} onClose={() => setCandOpen(false)} title="Submit Candidate" testId="candidate-modal">
        {candTargetPosition && (
          <form onSubmit={submitCandidate} className="space-y-4">
            <div className="bg-brand-light border border-brand/20 rounded-md p-3 text-sm">
              <div className="font-semibold text-brand">{candTargetPosition.position_name}</div>
              <div className="text-xs text-gray-600">{candTargetPosition.client_company}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name *" value={candForm.name} onChange={(v) => setCandForm({ ...candForm, name: v })} required testId="cand-name" />
              <Field label="Email" type="email" value={candForm.email} onChange={(v) => setCandForm({ ...candForm, email: v })} testId="cand-email" />
              <Field label="Phone" value={candForm.phone} onChange={(v) => setCandForm({ ...candForm, phone: v })} testId="cand-phone" />
              <Field label="Location" value={candForm.location} onChange={(v) => setCandForm({ ...candForm, location: v })} testId="cand-location" />
              <Field label="Rate" value={candForm.rate} onChange={(v) => setCandForm({ ...candForm, rate: v })} testId="cand-rate" />
            </div>
            <div>
              <label className="label-base">Resume (PDF/DOCX)</label>
              <label className="mt-1 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-8 hover:border-brand hover:bg-brand-light/30 transition cursor-pointer">
                <Upload size={20} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {candResume ? candResume.name : "Click to upload or drag a file"}
                </span>
                <input type="file" accept=".pdf,.doc,.docx" hidden onChange={(e) => setCandResume(e.target.files?.[0] || null)} data-testid="cand-resume-input" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
              <button type="button" className="btn-secondary" onClick={() => setCandOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={candSubmitting} data-testid="cand-submit">
                {candSubmitting ? <Loader2 size={16} className="animate-spin" /> : "Submit"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4 ${className}`}>{children}</th>;
}
function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <label className="block">
      <span className="label-base">{label}</span>
      <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-base" data-testid={testId} />
    </label>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-900">{value || "—"}</div>
    </div>
  );
}
